import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Loader2 } from 'lucide-react';
import type { Bank, Merchant } from '../../types';

interface DatasetMapping {
  id: string;
  name: string;
  dataset: {
    id: string;
    name: string;
    site_id: number;
    date_range: string;
    start_date: string;
    end_date: string;
  };
  merchant: {
    id: string;
    name: string;
  };
  bank_mappings: Record<string, {
    bank_id: string;
    bank_name: string;
    bank_alias: string;
  }>;
}

export function EditDatasetMapping() {
  const { mappingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [mapping, setMapping] = useState<DatasetMapping | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    merchantId: '',
    bankMappings: {} as Record<string, string>
  });

  useEffect(() => {
    loadData();
  }, [mappingId]);

  const loadData = async () => {
    try {
      const [merchantsResult, banksResult, mappingResult] = await Promise.all([
        supabase.from('merchants').select('*').order('name'),
        supabase.from('banks').select('*').eq('deleted', false).order('name'),
        supabase
          .from('dataset_mappings')
          .select(`
            id,
            name,
            dataset:dataset_id (
              id,
              name,
              site_id,
              date_range,
              start_date,
              end_date
            ),
            merchant:merchant_id (
              id,
              name
            ),
            bank_mappings:dataset_mapping_bank_mappings (
              dataset_bank_name,
              bank:bank_id (
                id,
                name,
                alias
              )
            )
          `)
          .eq('id', mappingId)
          .single()
      ]);

      if (merchantsResult.error) throw merchantsResult.error;
      if (banksResult.error) throw banksResult.error;
      if (mappingResult.error) throw mappingResult.error;

      setMerchants(merchantsResult.data || []);
      setBanks(banksResult.data || []);

      const transformedMapping = {
        ...mappingResult.data,
        bank_mappings: mappingResult.data.bank_mappings.reduce((acc: Record<string, any>, m: any) => ({
          ...acc,
          [m.dataset_bank_name]: {
            bank_id: m.bank.id,
            bank_name: m.bank.name,
            bank_alias: m.bank.alias
          }
        }), {})
      };

      setMapping(transformedMapping);
      setFormData({
        name: transformedMapping.name,
        merchantId: transformedMapping.merchant.id,
        bankMappings: Object.entries(transformedMapping.bank_mappings).reduce((acc, [key, value]: [string, any]) => ({
          ...acc,
          [key]: value.bank_id
        }), {})
      });
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mapping) return;

    setSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('dataset_mappings')
        .update({
          name: formData.name.trim(),
          merchant_id: formData.merchantId
        })
        .eq('id', mapping.id);

      if (updateError) throw updateError;

      const { error: deleteError } = await supabase
        .from('dataset_mapping_bank_mappings')
        .delete()
        .eq('dataset_mapping_id', mapping.id);

      if (deleteError) throw deleteError;

      const bankMappingRows = Object.entries(formData.bankMappings).map(([datasetBankName, bankId]) => ({
        dataset_mapping_id: mapping.id,
        dataset_bank_name: datasetBankName,
        bank_id: bankId
      }));

      if (bankMappingRows.length > 0) {
        const { error: insertError } = await supabase
          .from('dataset_mapping_bank_mappings')
          .insert(bankMappingRows);

        if (insertError) throw insertError;
      }

      navigate(`/datasets/${mapping.dataset.id}`);
    } catch (err) {
      console.error('Error updating mapping:', err);
      setError(err instanceof Error ? err.message : 'Failed to update mapping');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !mapping) {
    return (
      <div className="text-center py-12">
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
          {error || 'Mapping not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link
          to={`/datasets/${mapping.dataset.id}`}
          className="flex items-center text-sm text-indigo-600 hover:text-indigo-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to {mapping.dataset.name}
        </Link>
      </div>

      <div className="bg-white shadow-lg rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900">Edit Dataset Mapping</h1>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Mapping Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700">Dataset</h3>
            <div className="mt-2 bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-900">{mapping.dataset.name}</p>
              <p className="text-xs text-gray-500">
                {mapping.dataset.date_range === '3days' ? 'Last 3 Days' : 'Last Month'}
              </p>
              <p className="text-xs text-gray-500">
                {new Date(mapping.dataset.start_date).toLocaleDateString()} - {new Date(mapping.dataset.end_date).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="merchant" className="block text-sm font-medium text-gray-700">
              Select Merchant
            </label>
            <select
              id="merchant"
              value={formData.merchantId}
              onChange={(e) => setFormData(prev => ({ ...prev, merchantId: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            >
              <option value="">Select a merchant...</option>
              {merchants.map((merchant) => (
                <option key={merchant.id} value={merchant.id}>
                  {merchant.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">Map Banks</h3>
            <div className="space-y-4">
              {Object.entries(mapping.bank_mappings).map(([datasetBank]) => (
                <div key={datasetBank} className="flex items-center space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {datasetBank}
                    </label>
                  </div>
                  <div className="flex-1">
                    <select
                      value={formData.bankMappings[datasetBank] || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bankMappings: {
                          ...prev.bankMappings,
                          [datasetBank]: e.target.value
                        }
                      }))}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                    >
                      <option value="">Map to bank...</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name} ({bank.alias})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={() => navigate(`/datasets/${mapping.dataset.id}`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}